import { useEffect, useState } from 'react';
import { Schema } from 'rsuite';
import serverErrorHandler from 'services/serverError.service';
import useUserStore from 'store/useUserStore';
import { getFormValidationStatus } from 'utils/form.util';
import { FirebaseError } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  linkWithCredential,
  OAuthCredential,
} from 'firebase/auth';
import { openErrorToaster } from 'services/toast.service';
import {
  consumePendingRedirectCredential,
} from 'utils/redirectCredentialStore';
import { requestAuthSessionSync } from 'utils/authSessionSync';

// Extract schema types for form validation
const { StringType } = Schema.Types;

/**
 * Define validation model for login form.
 * User must provide a valid email and password
 * in order to submit the form.
 *
 * rsuite(5.5.2): https://rsuitejs.com/components/form-validation/
 */
const model = Schema.Model({
  email: StringType()
    .isRequired('Please enter a valid email.')
    .isEmail('Please enter a valid email.'),
  password: StringType().isRequired('Please enter your password.'),
});

const INIT_FORM = { email: '', password: '', saveEmail: [] };

/** Android browsers block OAuth popups — use redirect flow instead. */
const shouldUseRedirect = () => /Android/i.test(navigator.userAgent);

export default function useSignIn() {
  const [formValue, setFormValue] = useState<any>(INIT_FORM); // set default form values
  const [isLoading, setIsLoading] = useState(false); // flag for submission process
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState<OAuthCredential | null>(null);
  const [pendingGoogleEmail, setPendingGoogleEmail] = useState<string | null>(null);

  // get stored email
  const storedEmail = useUserStore(s => s.storedEmail);

  // set stored login email in form state
  useEffect(() => {
    let mounted = true;

    if (mounted && storedEmail)
      setFormValue({
        ...INIT_FORM,
        saveEmail: ['true'],
        email: storedEmail,
      });

    return () => {
      mounted = false;
    };
  }, [storedEmail]);

  // On mount, check if useAuth stored a pending redirect credential
  // (happens when Android redirect flow hits account-exists error).
  useEffect(() => {
    const { credential, email } = consumePendingRedirectCredential();
    if (credential && email) {
      setPendingGoogleCredential(credential);
      setPendingGoogleEmail(email);
    }
  }, []);

  /**
   * Makes server request to validate user credentials.
   * Users with valid credentials are authenticated and
   * redirect to the authenticated routes
   */
  const submit = async () => {
    try {
      // verify that form is valid
      if (getFormValidationStatus(model.check(formValue))) {
        const auth = getAuth();

        // show spinner
        setIsLoading(true);

        // make copy of form value
        const payload = {
          email: formValue.email,
          password: formValue.password,
        };

        // Remove saveEmail key - it is not needed for login api request
        // @ts-ignore
        delete payload.saveEmail;

        setIsLoading(true);

        // set persistance to data
        await setPersistence(auth, browserLocalPersistence);

        // login using firebase auth
        await signInWithEmailAndPassword(
          auth,
          payload.email,
          payload.password,
        );

        // update stored email value if user checked off 'remember me'
        const { setStoredEmail, clearStoredEmail } = useUserStore.getState();
        if (formValue.saveEmail.length > 0 && formValue.saveEmail[0])
          setStoredEmail(formValue.email);
        else clearStoredEmail();
      }
    } catch (e) {
      // hide spinner
      setIsLoading(false);
      serverErrorHandler(e);
    }
  };

  /**
   * Authenticates the user with Google OAuth.
   * Uses redirect on Android (popups blocked) and popup on all other browsers.
   * If the Google email already has an email/password Firebase account, captures
   * the pending credential so the user can link the two accounts by entering their password.
   */
  const signInWithGoogle = async () => {
    try {
      const auth = getAuth();
      setIsLoading(true);

      await setPersistence(auth, browserLocalPersistence);

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      if (shouldUseRedirect()) {
        // Android: popup is blocked — redirect and handle result on next load via useAuth
        await signInWithRedirect(auth, provider);
        return;
      }

      const result = await signInWithPopup(auth, provider);
      if (result.user) requestAuthSessionSync(result.user);
    } catch (e: unknown) {
      setIsLoading(false);
      if (e instanceof FirebaseError) {
        if (e.code === 'auth/account-exists-with-different-credential') {
          const credential = GoogleAuthProvider.credentialFromError(e);
          const email = (e.customData?.email as string) ?? null;
          setPendingGoogleCredential(credential);
          setPendingGoogleEmail(email);
          return;
        }
        // User dismissed the popup — no error toast needed
        if (
          e.code === 'auth/popup-closed-by-user' ||
          e.code === 'auth/cancelled-popup-request'
        ) {
          return;
        }
      }
      serverErrorHandler(e);
    }
  };

  /**
   * Completes Google account linking for users who already have an email/password account.
   * Signs in with the existing password, then links the pending Google credential.
   */
  const completeLinking = async (password: string) => {
    if (!pendingGoogleCredential || !pendingGoogleEmail) return;
    try {
      setIsLoading(true);
      const auth = getAuth();
      const result = await signInWithEmailAndPassword(
        auth,
        pendingGoogleEmail,
        password,
      );
      await linkWithCredential(result.user, pendingGoogleCredential);
      setPendingGoogleCredential(null);
      setPendingGoogleEmail(null);
      requestAuthSessionSync(result.user);
    } catch (e) {
      setIsLoading(false);
      serverErrorHandler(e);
    }
  };

  const cancelLinking = () => {
    setPendingGoogleCredential(null);
    setPendingGoogleEmail(null);
  };

  return {
    formValue,
    isLoading,
    model,
    submit,
    signInWithGoogle,
    setFormValue,
    pendingGoogleEmail,
    completeLinking,
    cancelLinking,
  };
}
